/* eslint-disable @typescript-eslint/naming-convention */
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * Expected client-side HTTP responses that are normal app flow, not bugs, and
 * are already handled by the UI: 401 expired session, 403 forbidden, 410 expired
 * guest session, 422 validation rejection, 429 rate limit. Reporting these to
 * Sentry only creates noise (historically the single largest source of "errors").
 */
export const EXPECTED_CLIENT_ERROR_STATUSES = [401, 403, 410, 422, 429];

/**
 * Best-effort extraction of an HTTP status from a Sentry event so we can drop
 * expected client errors. Covers both manually-captured RTK errors (extra.status
 * / extra.payload.status) and unhandled promise rejections of a raw API error
 * object, which Sentry serialises into extra.__serialized__.status.
 */
const extractHttpStatus = (event: Sentry.Event): number | undefined => {
  const extra = event.extra as Record<string, any> | undefined;
  if (!extra) return undefined;
  // __serialized__ is Sentry's own key for serialised unhandled-rejection
  // values; dot access keeps dot-notation happy, disable covers underscore rule.
  // eslint-disable-next-line no-underscore-dangle
  const serialized = extra.__serialized__;
  const candidates = [extra.status, extra.payload?.status, serialized?.status];
  return candidates.find((c) => typeof c === 'number');
};

/**
 * Determine if we're in a production environment.
 * For native apps: always consider production (they're only distributed via app stores)
 * For web: check the hostname against known production hosts
 */
const isProductionEnvironment = (): boolean => {
  // Local development - never enable
  if (process.env.NODE_ENV === 'development') {
    return false;
  }

  // Native apps (iOS/Android) - these are only distributed via app stores, so always production
  if (Capacitor.isNativePlatform()) {
    return true;
  }

  // Web: check hostname
  const productionHosts = ['doqit-react-prod.herokuapp.com', 'app.doqit.io'];
  return productionHosts.includes(window.location.host);
};

/**
 * Get the current environment name for Sentry tagging.
 */
const getEnvironmentName = (): string => {
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }

  if (Capacitor.isNativePlatform()) {
    return 'production'; // Native apps are always production
  }

  const productionHosts = ['doqit-react-prod.herokuapp.com', 'app.doqit.io'];
  if (productionHosts.includes(window.location.host)) {
    return 'production';
  }

  return 'staging';
};

/**
 * Errors thrown before Sentry is armed, held so they can be replayed once it is.
 *
 * initSentry is async: on native it awaits a Capacitor bridge round-trip for
 * App.getInfo() before Sentry.init() ever runs, while index.tsx has already
 * called root.render() synchronously. So React's entire first render happens
 * inside a window where Sentry does not exist yet, and anything thrown there
 * is lost.
 *
 * That window is precisely where a white screen on launch lives, which is why
 * those reports have never had a Sentry event to go with them. These listeners
 * are installed synchronously at module load, so they are in place before any
 * of the app renders, and whatever they catch is replayed after init.
 */
const preInitErrors: Array<{ error: unknown; kind: string }> = [];
let sentryReady = false;

// index.tsx suppresses these in the capture phase, but these listeners are
// registered first (this module is imported before that code runs), so the
// filter has to exist here too or the buffer fills with benign layout noise.
const isBenignResizeObserverError = (value: unknown): boolean => {
  const message = value instanceof Error ? value.message : String(value ?? '');
  return message.startsWith('ResizeObserver loop');
};

const bufferEarlyError = (error: unknown, kind: string) => {
  if (sentryReady) return;
  if (isBenignResizeObserverError(error)) return;
  // Bounded: a render loop could otherwise push thousands before init lands.
  if (preInitErrors.length >= 10) return;
  preInitErrors.push({ error, kind });
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => bufferEarlyError(e.error ?? e.message, 'onerror'));
  window.addEventListener('unhandledrejection', (e) => bufferEarlyError(e.reason, 'unhandledrejection'));
}

const flushEarlyErrors = () => {
  sentryReady = true;
  while (preInitErrors.length) {
    const entry = preInitErrors.shift();
    if (!entry) break;
    const err = entry.error instanceof Error ? entry.error : new Error(String(entry.error));
    Sentry.captureException(err, {
      tags: { pre_init: 'true' },
      extra: { kind: entry.kind },
    });
  }
};

/**
 * Initialize Sentry error tracking.
 * Must be called before React renders.
 */
export const initSentry = async () => {
  // Get DSN from build-time env var (works for both web and native)
  const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

  // Don't initialize if not in production or DSN is not set
  if (!isProductionEnvironment()) {
    // eslint-disable-next-line no-console
    console.log('[Sentry] Skipping initialization (not production environment)');
    return;
  }

  if (!SENTRY_DSN) {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] DSN not configured - error tracking disabled');
    return;
  }

  // Get native app version from Info.plist (which has MARKETING_VERSION)
  let release = 'web';
  if (Capacitor.isNativePlatform()) {
    try {
      const appInfo = await App.getInfo();
      release = `com.dr.doqit@${appInfo.version}+${appInfo.build}`;
      // e.g., "com.dr.doqit@1.73+73"
    } catch (error) {
      // Fall back to 'native' if we can't get version
      release = 'native';
    }
  }

  // eslint-disable-next-line no-console
  console.log(`[Sentry] Initializing for ${getEnvironmentName()} (platform: ${Capacitor.getPlatform()})`);

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: getEnvironmentName(),
    release,

    // Error tracking settings - disable performance tracing
    tracesSampleRate: 0,

    // Add platform tag
    initialScope: {
      tags: {
        platform: Capacitor.getPlatform(),
      },
    },

    // Filter out noisy errors
    ignoreErrors: [
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'ChunkLoadError',
      'ResizeObserver loop limit exceeded',
    ],

    // Scrub sensitive data before sending
    beforeSend(event) {
      // Drop expected client errors (handled by the UI) so they don't pollute
      // error tracking — e.g. 429 rate limits, 422 validation rejections.
      const status = extractHttpStatus(event);
      if (status && EXPECTED_CLIENT_ERROR_STATUSES.includes(status)) {
        return null;
      }

      // Remove IP address for privacy
      if (event.user) {
        // eslint-disable-next-line no-param-reassign
        event.user = { ...event.user, ip_address: undefined };
      }
      return event;
    },
  });

  // Anything that blew up while we were getting here still gets reported.
  flushEarlyErrors();
};

/**
 * Set user context for error tracking.
 * Call after successful login.
 */
export const setSentryUser = (user: { id: string; email?: string }) => {
  Sentry.setUser({ id: user.id, email: user.email });
};

/**
 * Clear user context.
 * Call on logout.
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Manually capture an error with optional context.
 */
export const captureError = (
  error: Error,
  context?: Record<string, unknown>,
  fingerprint?: string[],
) => {
  Sentry.captureException(error, { extra: context, fingerprint });
};

/**
 * Manually capture a message.
 */
export const captureMessage = (
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
) => {
  Sentry.captureMessage(message, level);
};

export default {
  initSentry,
  setSentryUser,
  clearSentryUser,
  captureError,
  captureMessage,
};
